import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Space, message, Popconfirm, Typography, Col, Tooltip, Modal, Tag } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  SearchOutlined,
  AppstoreOutlined,
  ReloadOutlined,
  ShoppingOutlined,
  HistoryOutlined
} from "@ant-design/icons";
import AddProductOption from "../../component/AddProductOption";
import ProductOptionServ from "../../service/ProductOptionServ";
import CategoryServ from "../../service/CategoryServ";
import SubCategoryServ from "../../service/SubCategoryServ";
import ShopServ from "../../service/ShopServ";
import "../../css/productOption.css";
import "../../css/shops.css";
import EmptyState from "../../util/EmptyState";
import Breadcrumbs from "../../component/Breadcrumbs";
import { FilterBar, SecondaryFilterBar } from "../../component/ui/FilterBar";
import SearchInput from "../../component/ui/SearchInput";
import { PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";
import TableImage from "../../component/ui/TableImage";
import SafeImage, { getImageUrl as getNormalizedImageUrl } from "../../component/SafeImage";
import DataTable from "../../component/ui/DataTable";
import FormSelect from "../../component/ui/FormSelect";
import { Select, Input } from "antd";
import { useAdminTable } from "../hooks/useAdminTable";

const { Text } = Typography;

const ProductOptions = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [shops, setShops] = useState([]);
  const [addProductOption, setAddProductOption] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [productOptionDetails, setProductOptionDetails] = useState(null);
  const [viewModal, setViewModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [subCategories, setSubCategories] = useState([]);

  const fetchSubCategories = async () => {
    try {
      const response = await SubCategoryServ.index({ limit: "all" });
      setSubCategories(response?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await CategoryServ.index({ filter: JSON.stringify({ parentId: null }) });
      setCategories(response?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await ShopServ.adminIndex({ limit: "all" });
      setShops(response?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchProductOptions = async (params) => {
    const filter = {};
    if (params.category && params.category !== "All") filter.category = params.category;
    if (params.subCategory && params.subCategory !== "All") filter.subCategory = params.subCategory;
    if (params.shopId && params.shopId !== "All") filter.shopId = params.shopId;
    if (params.search) filter.searchText = params.search;

    const apiParams = {
      limit: params.limit,
      skip: (params.page - 1) * params.limit,
      sort: JSON.stringify({ updatedAt: -1 }),
      filter: JSON.stringify(filter),
      populate: "productId,shopId,productId.shopId,productId.categoryId"
    };
    const response = await ProductOptionServ.index(apiParams);
    const data = response?.data || response || [];
    const total = response?.total || (Array.isArray(data) ? data.length : 0);

    const normalized = (data || []).map((it) => {
      const product = it.productId;
      let displayValue = Array.isArray(it.option) ? it.option.join(", ") : (it.option || "");
      let valuesArray = Array.isArray(it.option) ? it.option : (it.option ? [it.option] : []);
      let variantCount = 0;

      if (product && Array.isArray(product.options) && product.options.length > 0) {
        let matchedGroup = product.options.find(
          opt => opt.name?.toLowerCase() === it.name?.toLowerCase()
        );
        if (!matchedGroup && product.options.length === 1) matchedGroup = product.options[0];
        if (matchedGroup && Array.isArray(matchedGroup.variants)) {
          displayValue = matchedGroup.variants.map(v => v.value).join(", ");
          valuesArray = matchedGroup.variants.map(v => v.value);
          variantCount = matchedGroup.variants.length;
        }
      }

      if (!displayValue || displayValue === "" || displayValue === "N/A") {
        if (product && Array.isArray(product.variants) && product.variants.length > 0) {
          if (product.variantType?.toLowerCase() === it.name?.toLowerCase() || !product.variantType) {
            displayValue = product.variants.map(v => v.value).join(", ");
            valuesArray = product.variants.map(v => v.value);
            variantCount = product.variants.length;
          }
        }
      }

      return {
        id: it._id || it.id,
        // ✅ Permanent business ID from ProductOption collection
        productOptionId: it.productOptionId || it.raw?.productOptionId || "",
        name: it.name,
        value: displayValue || "N/A",
        values: valuesArray,
        productName: product?.name || it.productName || "N/A",
        productImage: product?.imageUrl || null,
        productId: product?._id || product?.id || it.productId,
        // ✅ Per-option pricing/stock from the ProductOption record itself
        // Falls back to product root values for backward compat
        price: it.price ?? it.raw?.price ?? product?.price ?? 0,
        mrp: it.mrp ?? it.raw?.mrp ?? product?.mrp ?? product?.price ?? 0,
        stock: it.stock ?? it.raw?.stock ?? product?.stock ?? 0,
        isActive: it.isActive !== undefined ? it.isActive : (it.raw?.isActive !== undefined ? it.raw.isActive : true),
        variantCount: variantCount || (valuesArray.length),
        category: (product?.categoryId?.name || product?.category || "N/A"),
        shop: (() => {
          const shopObj = (it.shopId && typeof it.shopId === 'object') ? it.shopId : (product?.shopId && typeof product.shopId === 'object' ? product.shopId : null);
          return {
            id: shopObj?._id || shopObj?.id || it.shopId || (typeof product?.shopId === 'string' ? product.shopId : product?.shopId?._id),
            name: shopObj?.name || it.shopName || product?.shopName || "N/A",
            imageUrl: shopObj?.imageUrl || shopObj?.logo || shopObj?.shopImage || it.shopImage || it.shopImageUrl || it.logo || it.shopLogo || it.image || it.imageUrl || product?.shopImage || product?.shopId?.imageUrl || it.shopId?.imageUrl
          };
        })(),
        raw: it
      };
    });
    return { data: normalized, total };
  };

  const {
    data: productOptions,
    setData: setProductOptions,
    total: totalProductOption,
    loading,
    params,
    setParams,
    handleSearch,
    handleTableChange,
    refresh
  } = useAdminTable(fetchProductOptions, "admin_product_options", { page: 1, limit: 10, category: "All", subCategory: "All", shopId: "All" });

  const searchText = params.search;
  const category = params.category;
  const subCategory = params.subCategory;
  const shopId = params.shopId;

  const handleReset = () => {
    setParams({ page: 1, limit: 10, category: "All", subCategory: "All", shopId: "All", search: "" });
  };

  useEffect(() => {
    fetchCategories();
    fetchSubCategories();
    fetchShops();
  }, []);

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setViewModal(true);
  };

  const handleAddProductOption = () => {
    setProductOptionDetails(null);
    setIsEditing(false);
    setAddProductOption(true);
  };

  const handleEditProductOption = (record) => {
    setProductOptionDetails(record);
    setIsEditing(true);
    setAddProductOption(true);
  };

  const handleDeleteProductOption = async (record) => {
    try {
      await ProductOptionServ.remove(record.id);
      message.success("Product option removed successfully");
      fetchProductOptions();
    } catch (error) {
      console.error(error);
      message.error("Failed to remove product option");
    }
  };

  const refreshProductOptions = async (isRefresh) => {
    setAddProductOption(false);
    if (isRefresh) refresh();
  };

  const columns = [
    {
      title: "IMAGE",
      dataIndex: "productImage",
      key: "productImage",
      width: 70,
      align: "center",
      render: (img, record) => <TableImage src={getNormalizedImageUrl(img)} alt="product" size={55} onClick={() => handleViewDetails(record)} />,
    },
    {
      title: "OPTION ID",
      dataIndex: "productOptionId",
      key: "productOptionId",
      width: 110,
      render: (pid) => pid
        ? <Tag style={{ fontFamily: 'monospace', fontSize: 11, fontWeight: 700, borderRadius: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d' }}>{pid}</Tag>
        : <span style={{ color: '#cbd5e1', fontSize: 11 }}>—</span>
    },
    {
      title: "PRODUCT / OPTION",
      dataIndex: "productName",
      key: "productName",
      width: 200,
      render: (name, record) => (
        <div style={{ display: "flex", flexDirection: "column", cursor: "pointer" }}>
          <span style={{ fontWeight: 700, color: "#1e293b", fontSize: "13px" }}>{name}</span>
          <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, marginTop: 2 }}>Group: {record.name}</span>
        </div>
      ),
    },
    {
      title: "PRICE",
      dataIndex: "price",
      key: "price",
      width: 80,
      render: (price, record) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 700, color: "#FF8800", fontSize: 13 }}>₹{Number(price).toFixed(2)}</span>
          <span style={{ color: '#94a3b8', textDecoration: 'line-through', fontSize: 11 }}>₹{Number(record.mrp).toFixed(2)}</span>
        </div>
      )
    },
    {
      title: "STOCK",
      dataIndex: "stock",
      key: "stock",
      width: 70,
      align: "center",
      render: (stock) => {
        const n = Number(stock);
        const color = n === 0 ? '#ef4444' : n <= 5 ? '#f59e0b' : '#16a34a';
        return <span style={{ fontWeight: 700, color, fontSize: 13 }}>{n}</span>;
      }
    },
    {
      title: "VARIANTS",
      dataIndex: "variantCount",
      key: "variantCount",
      width: 75,
      align: "center",
      render: (count) => <Tag style={{ fontWeight: 700, borderRadius: 8, background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c' }}>{count} items</Tag>
    },
    {
      title: "STATUS",
      dataIndex: "isActive",
      key: "isActive",
      width: 80,
      align: "center",
      render: (active) => active
        ? <Tag style={{ borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', fontWeight: 700 }}>Active</Tag>
        : <Tag style={{ borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontWeight: 700 }}>Inactive</Tag>
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 80,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleViewDetails(record)}
          style={{
            borderRadius: 6,
            fontWeight: 600,
            background: '#fff',
            color: '#004754',
            border: '1px solid #e4e4e7',
            boxShadow: '0 0 0 1px #e4e4e7',
            height: 'auto',
            padding: '2px 10px',
            fontSize: 12,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const topRef = React.useRef(null);

  return (
    <div className="product-option-container pcu-padding-24" ref={topRef}>
      <div className="pcu-margin-bottom-16">
        <Breadcrumbs />
      </div>

      <div className="options-container" style={{ padding: "24px", background: "white", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        {/* Header Section */}
        <div className="options-header" style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "#e0e7ff",
                borderRadius: "12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <AppstoreOutlined style={{ fontSize: "22px", color: "#4776E6" }} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: "20px", fontWeight: 800, color: "#1a1d2e", letterSpacing: "-0.5px" }}>Product Options</h1>
              <p style={{ margin: 0, fontSize: "12px", color: "#9ca3af", fontWeight: 500 }}>{totalProductOption} options listed</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filter-section" style={{ marginBottom: "24px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr auto", gap: "12px", alignItems: "flex-end" }}>
          <div>
            <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Search Options</span>
            <div style={{ position: "relative" }}>
              <SearchOutlined style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", zIndex: 1 }} />
              <Input
                placeholder="Search by option n..."
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ height: "40px", paddingLeft: "36px", borderRadius: "10px", border: "1px solid #e2e8f0" }}
              />
            </div>
          </div>
          <div>
            <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Shop Name</span>
            <Select
              placeholder="All Shops"
              style={{ width: "100%", height: "40px" }}
              value={shopId}
              onChange={(v) => setParams(prev => ({ ...prev, shopId: v, page: 1 }))}
            >
              <Select.Option value="All">All Shops</Select.Option>
              {shops.map(s => <Select.Option key={s._id} value={s._id}>{s.name}</Select.Option>)}
            </Select>
          </div>
          <div>
            <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>Category</span>
            <Select
              placeholder="All Categories"
              style={{ width: "100%", height: "40px" }}
              value={category}
              onChange={(v) => setParams(prev => ({ ...prev, category: v, page: 1 }))}
            >
              <Select.Option value="All">All Categories</Select.Option>
              {categories.map(c => <Select.Option key={c._id} value={c.name}>{c.name}</Select.Option>)}
            </Select>
          </div>
          <div>
            <span style={{ color: "#94a3b8", fontSize: "10px", fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>SubCategory</span>
            <Select
              placeholder="All SubCategories"
              style={{ width: "100%", height: "40px" }}
              value={subCategory}
              onChange={(v) => setParams(prev => ({ ...prev, subCategory: v, page: 1 }))}
            >
              <Select.Option value="All">All SubCategories</Select.Option>
              {subCategories.map(sc => <Select.Option key={sc._id} value={sc.name}>{sc.name}</Select.Option>)}
            </Select>
          </div>
          <button
            onClick={handleReset}
            style={{
              height: "40px",
              padding: "0 20px",
              borderRadius: "10px",
              border: "1px solid #e2e8f0",
              background: "white",
              color: "#64748b",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer"
            }}
          >
            <ReloadOutlined /> Reset
          </button>
        </div>

        <div className="pcu-border-top pcu-margin-x-n24">
          <DataTable
            data={productOptions}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{
              current: params.page,
              pageSize: params.limit,
              total: totalProductOption,
              onShowSizeChange: (c, s) => setParams(prev => ({ ...prev, limit: s, page: 1 })),
              onChange: (p) => {
                setParams(prev => ({ ...prev, page: p }));
                topRef.current?.scrollIntoView({ behavior: 'smooth' });
              },
            }}
            locale={{
              emptyText: (
                <EmptyState
                  title="No Options Found"
                  description="No product options matched your filters."
                  gradient="purple"
                  showAction={false}
                />
              )
            }}
          />
        </div>
      </div>

      {addProductOption && (
        <AddProductOption
          open={addProductOption}
          optionDetails={productOptionDetails}
          refreshOptions={refreshProductOptions}
          isEditing={isEditing}
        />
      )}

      {/* Details Modal (Image 3 Style) */}
      <Modal
        open={viewModal}
        onCancel={() => setViewModal(false)}
        footer={null}
        width={650}
        className="ss-modal"
        centered
        styles={{ body: { padding: 0 } }}
      >
        {selectedRecord && (
          <div className="ss-modal-body">
            {/* Modal Hero */}
            <div className="ss-modal-hero">
              <div className="ss-modal-img" style={{ width: 100, height: 100 }}>
                <img
                  src={getNormalizedImageUrl(selectedRecord.productImage)}
                  alt={selectedRecord.productName}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div className="ss-modal-info">
                <h2 className="ss-modal-name">{selectedRecord.productName}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '16px', fontWeight: 800, color: '#FF8800' }}>₹{selectedRecord.price.toFixed(2)}</span>
                  <span style={{ fontSize: '13px', color: '#94a3b8', textDecoration: 'line-through' }}>₹{selectedRecord.mrp.toFixed(2)}</span>
                </div>
                <div className="ss-modal-tags">
                  <span className="ss-tag ss-tag-blue" style={{ background: '#e0f2fe', color: '#0369a1' }}>{selectedRecord.category}</span>
                  <span className="ss-tag ss-tag-orange" style={{ background: '#fff7ed', color: '#c2410c' }}>Option: {selectedRecord.name}</span>
                  <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>{selectedRecord.variantCount} Variants</span>
                </div>
              </div>
            </div>

            {/* Detail Grid */}
            <div className="ss-modal-details" style={{ padding: '20px 24px' }}>
              {selectedRecord.productOptionId && (
                <div className="ss-modal-row">
                  <div className="ss-modal-label">Option ID</div>
                  <div className="ss-modal-value">
                    <Tag style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, borderRadius: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d' }}>
                      {selectedRecord.productOptionId}
                    </Tag>
                  </div>
                </div>
              )}
              <div className="ss-modal-row">
                <div className="ss-modal-label">Option Name</div>
                <div className="ss-modal-value" style={{ fontWeight: 700 }}>{selectedRecord.name}</div>
              </div>
              <div className="ss-modal-row">
                <div className="ss-modal-label">Values</div>
                <div className="ss-modal-value">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', justifyContent: 'flex-end' }}>
                    {selectedRecord.values.map((v, i) => (
                      <span key={i} style={{ padding: '2px 10px', background: '#e0f2fe', color: '#0369a1', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}>{v}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="ss-modal-row">
                <div className="ss-modal-label">Stock</div>
                <div className="ss-modal-value" style={{ fontWeight: 700, color: Number(selectedRecord.stock) === 0 ? '#ef4444' : '#16a34a' }}>
                  {selectedRecord.stock} units
                </div>
              </div>
              <div className="ss-modal-row">
                <div className="ss-modal-label">Total Variants</div>
                <div className="ss-modal-value" style={{ fontWeight: 700 }}>{selectedRecord.variantCount} Items</div>
              </div>
              <div className="ss-modal-row"
                onClick={() => selectedRecord.shop?.id && navigate(`/admin/shop/${selectedRecord.shop.id}`)}
                style={{ cursor: 'pointer', borderBottom: 'none' }}
              >
                <div className="ss-modal-label">Associated Shop</div>
                <div className="ss-modal-value">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <SafeImage
                      src={selectedRecord.shop?.imageUrl}
                      alt="shop"
                      style={{ width: 32, height: 32, borderRadius: '6px', border: '1px solid #f1f5f9' }}
                    />
                    <span style={{ fontWeight: 700, color: '#1e293b' }}>{selectedRecord.shop?.name || "N/A"}</span>
                    <ShoppingOutlined style={{ fontSize: 12, color: '#94a3b8', marginLeft: 4 }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="ss-modal-footer" style={{ justifyContent: "space-between" }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  icon={<EditOutlined />}
                  onClick={() => { setViewModal(false); handleEditProductOption(selectedRecord.raw); }}
                  style={{ fontWeight: 700, color: '#4776E6', height: '40px', borderRadius: '10px' }}
                >
                  Edit
                </Button>
                <Popconfirm
                  title="Are you sure to Remove this option group?"
                  onConfirm={() => { setViewModal(false); handleDeleteProductOption(selectedRecord.raw); }}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    style={{ fontWeight: 700, height: '40px', borderRadius: '10px', background: '#fff1f2', border: '1px solid #fecdd3' }}
                  >
                    Remove Option Group
                  </Button>
                </Popconfirm>
              </div>
              <button className="ss-modal-close-btn" onClick={() => setViewModal(false)} style={{ height: '40px' }}>
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProductOptions;
